import { useEffect, useRef } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { startWebRecording, useRecordAction } from "../dvr/recordAction.js";

interface ProgramContextMenuProps {
  /** The programme held, or — held on a channel row — what is on now (null when
   *  the guide has nothing for the channel). */
  program: Program | null;
  channel: Channel;
  /** Viewport coordinates of the press, so the menu opens at the thumb/cursor. */
  x: number;
  y: number;
  onClose: () => void;
  onPlay: () => void;
  onDetails: () => void;
  onMirror: () => void;
  /** Open "Choose guide" (the TV app, Phase 3 step 4); no item without it. */
  onChooseGuide?: () => void;
  /** Open the record dialog (Android app only). */
  onRecord?: () => void;
  /** Held on a CHANNEL rather than a programme: the same menu, but Record is
   *  greyed out — a recording is of a programme, and none was picked. */
  fromChannel?: boolean;
}

/**
 * Long-press / right-click menu on an EPG cell — the quick-action surface the
 * reference apps (SparkleTV, NexTV) put behind a press-and-hold, so favoriting
 * or recording never costs a trip through the detail dialog.
 *
 * Favoriting targets the CHANNEL, not the programme: a programme is a
 * time-boxed row in a feed that is replaced on every EPG refresh, so a
 * favorite pinned to it would silently evaporate. "Favorite this programme"
 * means "I want this channel", which is also what the reference apps do.
 */
export function ProgramContextMenu({ program, channel, x, y, onClose, onPlay, onDetails, onMirror, onChooseGuide, onRecord, fromChannel = false }: ProgramContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ open: true, onClose, rootRef: menuRef });
  const isFavorite = useFavoritesStore((s) => s.isFavorite("channel", channel.id));
  const isHidden = useFavoritesStore((s) => s.isHidden("channel", channel.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const toggleHidden = useFavoritesStore((s) => s.toggleHidden);

  // Keep the menu inside the viewport — a press near the right or bottom edge
  // would otherwise open a menu that is partly unreachable.
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const overflowX = rect.right - window.innerWidth + 8;
    const overflowY = rect.bottom - window.innerHeight + 8;
    if (overflowX > 0) el.style.left = `${Math.max(8, x - overflowX)}px`;
    if (overflowY > 0) el.style.top = `${Math.max(8, y - overflowY)}px`;
  }, [x, y]);

  return (
    <div className="epg-context-overlay" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        ref={menuRef}
        className="epg-context-menu"
        style={{ left: x, top: y }}
        onClick={(e) => e.stopPropagation()}
        role="menu"
        aria-label={program && !fromChannel ? `Actions for ${program.title} on ${channel.name}` : `Actions for ${channel.name}`}
        tabIndex={-1}
      >
        <div className="epg-context-header">
          <span className="epg-context-channel">{channel.number ? `${channel.number} ` : ""}{channel.name}</span>
          {program && <span className="epg-context-title">{program.title}</span>}
        </div>

        <button className="epg-context-item" role="menuitem" onClick={() => { onPlay(); onClose(); }}>
          <span aria-hidden="true">▶</span> Watch
        </button>

        <button
          className="epg-context-item"
          role="menuitem"
          aria-pressed={isFavorite}
          onClick={() => { toggleFavorite("channel", channel.id); onClose(); }}
        >
          <span aria-hidden="true">{isFavorite ? "★" : "☆"}</span>
          {isFavorite ? "Remove channel from favorites" : "Add channel to favorites"}
        </button>

        <button
          className="epg-context-item"
          role="menuitem"
          aria-pressed={isHidden}
          onClick={() => { toggleHidden("channel", channel.id); onClose(); }}
        >
          <span aria-hidden="true">{isHidden ? "👁" : "🚫"}</span>
          {isHidden ? "Unhide this channel" : "Hide this channel"}
        </button>

        {/* Record: the Android app's recorder for any programme; on the website,
            "Record now" for a programme airing now (into a file the viewer picks),
            and a greyed-out Record that says where scheduling works. Greyed out by
            aria-disabled, not disabled: a disabled button shows no hover text. */}
        {fromChannel || !program ? (
          <button className="epg-context-item epg-context-item--disabled" role="menuitem" aria-disabled="true" title="Pick a programme in the guide to record it">
            <span className="rec-dot rec-dot--off" aria-hidden="true" /> Record
            <span className="epg-context-soon">Pick a show</span>
          </button>
        ) : (
          <RecordItem program={program} channel={channel} onPlay={onPlay} onRecord={onRecord} onClose={onClose} />
        )}

        {program && (
          <button className="epg-context-item" role="menuitem" onClick={() => { onDetails(); onClose(); }}>
            <span aria-hidden="true">ⓘ</span> Programme details
          </button>
        )}

        {/* Section 16: cross-provider fallback mirror. Lives here so a pending
            source can get a backup without a trip through Settings. */}
        <button className="epg-context-item" role="menuitem" onClick={() => { onMirror(); onClose(); }}>
          <span aria-hidden="true">⇄</span> Mirror for fallback…
        </button>

        {/* Phase 3 step 4: pin the channel's guide by hand, and shift its times. */}
        {onChooseGuide && (
          <button className="epg-context-item" role="menuitem" onClick={() => { onChooseGuide(); onClose(); }}>
            <span aria-hidden="true">☰</span> Choose guide…
          </button>
        )}
      </div>
    </div>
  );
}

/** Record for a real programme — its own component so the record hook only
 *  ever runs with one (a menu held on a channel may have none). */
function RecordItem({ program, channel, onPlay, onRecord, onClose }: {
  program: Program;
  channel: Channel;
  onPlay: () => void;
  onRecord?: () => void;
  onClose: () => void;
}) {
  const record = useRecordAction(program, channel);
  return record.kind === "dvr" && onRecord ? (
    <button className="epg-context-item" role="menuitem" title={record.title} onClick={() => { onRecord(); onClose(); }}>
      <span className="rec-dot" aria-hidden="true" /> Record…
    </button>
  ) : record.kind === "now" ? (
    <button className="epg-context-item" role="menuitem" title={record.title} onClick={() => { void startWebRecording(program, channel, onPlay).then((started) => { if (started) onClose(); }); }}>
      <span className="rec-dot" aria-hidden="true" /> Record now…
    </button>
  ) : (
    <button className="epg-context-item epg-context-item--disabled" role="menuitem" aria-disabled="true" title={record.title}>
      <span className="rec-dot rec-dot--off" aria-hidden="true" /> Record
      <span className="epg-context-soon">Android app</span>
    </button>
  );
}
